import { Alert, Badge, Button, Checkbox, Drawer, Group, Loader, Stack, Text, TextInput } from '@mantine/core';
import { IconAlertTriangle, IconInbox, IconSearch } from '@tabler/icons-react';
import { useState } from 'react';
import { useConfirmedAdopt } from '../Adopt/AdoptConfirm.js';
import { useInbox } from '../../hooks/useInbox.js';
import { useIsMobile } from '../../hooks/useIsMobile.js';
import { errorMessage } from '../../utils/errors.js';
import type { OpenTarget } from '../Matrix/types.js';
import { SectionHeading } from '../SectionHeading/SectionHeading.js';
import { describeAdopts } from '../../domain/adopt.js';
import { countOf } from '../../domain/labels.js';
import { rowLabel } from '../../domain/rowLabel.js';
import { toggled } from '../../domain/toggled.js';
import { RecordLabel } from '../Diff/RecordLabel.js';
import { adoptAllRequest, adoptRequestsFor, filterInbox, groupInbox } from './inboxView.js';

/**
 * Inbox · <env>: what the environment holds that master does not, with checkboxes. Games sit
 * under their studio (`groupInbox`), master's or the inbox's own; ticking the studio ticks every
 * game under it — an inbox studio is adopted with them (that is what `studios.adopt` does anyway),
 * a studio master holds just has its games adopted. A row's title opens the entity drawer so a
 * person can look before adopting. Stays open after an adopt and refreshes through the global
 * invalidation; an empty inbox then shows the empty state.
 */
export const InboxDrawer = ({
    environment,
    studio,
    onClose,
    onOpenEntity,
}: {
    environment: string | null;
    studio?: string | null;
    onClose: () => void;
    onOpenEntity: (target: OpenTarget) => void;
}) => {
    const isMobile = useIsMobile();

    return (
        <Drawer
            opened={environment !== null}
            onClose={onClose}
            position="right"
            size={isMobile ? '100%' : 'xl'}
            title={
                <Group gap="xs">
                    <IconInbox size={16} stroke={1.7} />
                    <Text fw={600}>
                        Inbox · <span className="rgs-numeric">{environment}</span>
                        {studio && (
                            <>
                                {' '}
                                · <span className="rgs-numeric">{studio}</span>
                            </>
                        )}
                    </Text>
                </Group>
            }
        >
            {/* Keyed on the environment/studio pair, so a new inbox starts from nothing
                selected and no search text — a `useEffect` reset would run one render late. */}
            {environment && (
                <InboxContent
                    key={`${environment}/${studio ?? ''}`}
                    environment={environment}
                    studio={studio}
                    onOpenEntity={onOpenEntity}
                />
            )}
        </Drawer>
    );
};

const InboxContent = ({
    environment,
    studio,
    onOpenEntity,
}: {
    environment: string;
    studio?: string | null;
    onOpenEntity: (target: OpenTarget) => void;
}) => {
    const inbox = useInbox(environment, studio);
    const adoption = useConfirmedAdopt();
    const [search, setSearch] = useState('');
    const [studios, setStudios] = useState<Set<string>>(new Set());
    const [games, setGames] = useState<Set<string>>(new Set());
    const [configs, setConfigs] = useState<Set<string>>(new Set());

    const data = inbox.data ? filterInbox(inbox.data, search) : null;
    const groups = data ? groupInbox(data) : [];
    const total =
        (inbox.data?.studios.length ?? 0) + (inbox.data?.games.length ?? 0) + (inbox.data?.configs.length ?? 0);

    const requests = inbox.data ? adoptRequestsFor(environment, { studios, games, configs }, inbox.data) : [];
    // One confirmation for the whole selection, however many procedures it takes.
    const adoptSelected = () => adoption.adoptMany(requests);
    const adoptAll = () => adoption.adopt(adoptAllRequest(environment, studio, inbox.data?.games.length ?? 0, total));

    return (
        <Stack gap="md">
            <Text fz="sm" c="dimmed">
                Records this environment holds that master has no record of. Adopting copies them into master as this
                environment last reported them; no environment is written.
            </Text>
            {inbox.isLoading && <Loader />}
            {inbox.isError && (
                <Alert color="red" icon={<IconAlertTriangle size={16} stroke={1.7} />}>
                    <Group gap="sm">
                        <Text fz="sm">{errorMessage(inbox.error)}</Text>
                        <Button size="compact-xs" variant="light" onClick={inbox.refetch}>
                            Retry
                        </Button>
                    </Group>
                </Alert>
            )}
            {data && total === 0 && (
                <Text fz="sm" c="dimmed" ta="center" py="xl">
                    Nothing in <span className="rgs-numeric">{environment}</span> that master does not already have.
                </Text>
            )}
            {data && total > 0 && (
                <>
                    <TextInput
                        size="xs"
                        placeholder="Search by id or name"
                        aria-label="Search the inbox"
                        leftSection={<IconSearch size={14} stroke={1.7} />}
                        value={search}
                        onChange={(event) => setSearch(event.currentTarget.value)}
                    />
                    {groups.length > 0 && (
                        <Stack gap={4}>
                            <SectionHeading>Studios</SectionHeading>
                            {groups.map((group) => {
                                const ticked = studios.has(group.key);
                                return (
                                    <Stack key={group.key} gap={4}>
                                        <Group gap="sm" wrap="nowrap" align="center">
                                            <Checkbox
                                                size="xs"
                                                checked={ticked}
                                                onChange={() => setStudios(toggled(studios, group.key))}
                                                aria-label={`Select studio ${rowLabel(group.title, group.key).label}`}
                                            />
                                            <RecordLabel
                                                title={group.title}
                                                entityKey={group.key}
                                                layout="stacked"
                                                onOpen={() =>
                                                    onOpenEntity({ type: 'studio', key: group.key, environment })
                                                }
                                            />
                                            {!group.inMaster && (
                                                <Badge variant="light" color="gray" radius="sm" size="sm" tt="none">
                                                    not in master
                                                </Badge>
                                            )}
                                            <Text fz="xs" c="dimmed" ml="auto" style={{ whiteSpace: 'nowrap' }}>
                                                {countOf('game', group.games.length)}
                                            </Text>
                                        </Group>
                                        {group.games.length > 0 && (
                                            <Stack gap={4} className="rgs-perenv-rows" ml="md" mb={4}>
                                                {group.games.map((g) => (
                                                    <Group key={g.key} gap="sm" wrap="nowrap" align="center">
                                                        <Checkbox
                                                            size="xs"
                                                            checked={ticked || games.has(g.key)}
                                                            disabled={ticked}
                                                            onChange={() => setGames(toggled(games, g.key))}
                                                            aria-label={`Select game ${rowLabel(g.title, g.key).label}`}
                                                        />
                                                        <RecordLabel
                                                            title={g.title}
                                                            entityKey={g.key}
                                                            layout="stacked"
                                                            onOpen={() =>
                                                                onOpenEntity({ type: 'game', key: g.key, environment })
                                                            }
                                                        />
                                                    </Group>
                                                ))}
                                            </Stack>
                                        )}
                                    </Stack>
                                );
                            })}
                        </Stack>
                    )}
                    {data.configs.length > 0 && (
                        <Stack gap={4}>
                            <SectionHeading>Configs</SectionHeading>
                            {data.configs.map((c) => (
                                <Group key={c.key} gap="sm" wrap="nowrap" align="center">
                                    <Checkbox
                                        size="xs"
                                        checked={configs.has(c.key)}
                                        onChange={() => setConfigs(toggled(configs, c.key))}
                                        aria-label={`Select config ${rowLabel(c.title, c.key).label}`}
                                    />
                                    <RecordLabel
                                        title={c.title}
                                        entityKey={c.key}
                                        layout="stacked"
                                        onOpen={() => onOpenEntity({ type: 'config', key: c.key, environment })}
                                    />
                                </Group>
                            ))}
                        </Stack>
                    )}
                    <Group
                        justify="space-between"
                        wrap="wrap"
                        pt="sm"
                        style={{ borderTop: '1px solid var(--rgs-line)' }}
                    >
                        {requests.length > 0 && (
                            <Text fz="xs" c="dimmed">
                                {describeAdopts(requests)}
                            </Text>
                        )}
                        <Group gap="xs">
                            {requests.length > 0 && (
                                <Button
                                    size="compact-sm"
                                    variant="light"
                                    color="blue"
                                    loading={adoption.isPending}
                                    onClick={adoptSelected}
                                >
                                    Adopt selected
                                </Button>
                            )}
                            <Button
                                size="compact-sm"
                                variant="subtle"
                                color="blue"
                                disabled={adoption.isPending}
                                onClick={adoptAll}
                            >
                                Adopt all
                            </Button>
                        </Group>
                    </Group>
                </>
            )}
        </Stack>
    );
};
